import { useMutation, useQueryClient } from "@tanstack/react-query";
import { dropinApi } from "@bw/shared/api";
// The check-in REQUEST uses the spine's narrowed DropInCheckInRequest (its
// `payment_method` is the `single | package | membership` literal union the
// dropinApi.checkIn signature expects); the RESPONSE uses the generated DTO.
import type { DropInCheckInRequest } from "@bw/shared/types";
import type { DropInSessionResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import {
  currentDropInSessionsQueryOptions,
  reconcileFacilitySessions,
} from "./currentDropInSessionsQuery";

/**
 * Individual self check-in (POST /facilities/{id}/drop-in/check-in).
 *
 * The request carries no target user — the backend derives the player from the
 * token, so this is the CURRENT user's own check-in by construction (per the
 * non-delegable REALISM rule). On success the exact facility session is cached,
 * the cross-facility snapshot is reconciled, and dependent facility reads are
 * invalidated so every route reflects the just-created session.
 *
 * Errors are NOT thrown to a boundary: the ApiError surfaces on
 * `mutation.error` and the page maps the code (409 NoActiveDropIn /
 * AlreadyCheckedIn, 422 InvalidPaymentMethod) to an honest inline state.
 */
export function useCheckIn(facilityId: string) {
  const queryClient = useQueryClient();
  return useMutation<DropInSessionResponse, unknown, DropInCheckInRequest>({
    mutationKey: ["drop-in", "check-in", facilityId],
    mutationFn: (data: DropInCheckInRequest) =>
      dropinApi.checkIn(facilityId, data),
    onSuccess: async (session) => {
      const currentSessionsQuery = currentDropInSessionsQueryOptions();
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: currentSessionsQuery.queryKey,
          exact: true,
        }),
        queryClient.cancelQueries({
          queryKey: queryKeys.dropin.session(facilityId),
          exact: true,
        }),
      ]);

      queryClient.setQueryData(
        queryKeys.dropin.session(facilityId),
        session,
      );
      const current = queryClient.getQueryData<DropInSessionResponse[]>(
        currentSessionsQuery.queryKey,
      );
      if (current !== undefined) {
        queryClient.setQueryData<DropInSessionResponse[]>(
          currentSessionsQuery.queryKey,
          [
            session,
            ...current.filter(
              (candidate) => candidate.facility_id !== facilityId,
            ),
          ],
        );
      }

      try {
        const sessions = await queryClient.fetchQuery(currentSessionsQuery);
        reconcileFacilitySessions(queryClient, sessions);
      } catch {
        // Check-in succeeded; discovery will retry this unresolved/stale read.
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.queueStats(facilityId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.paymentOptions(facilityId),
      });
    },
  });
}
